import L from 'leaflet';

import { timeouts } from 'constants/values';

import ShapeType from '../PropTypes/ShapeType';

import { circle, point } from '3rd-party-wrappers/turf';

import { getMessage } from 'util/locale';

import { UNITS_OF_MEASURE } from 'constants/unitsOfMeasure';

// Functions for changing drawing state using via leaflet-draw but NOT with
// its built-in toolbar.

// Store and retrieve the drawing-specific context as a member of the map
// object. The context isn't used outside of this module and doesn't affect
// leaflet/leaflet-draw code.

export const _getContext = (map) => {
  let context = map._DrawingContext;
  let polygonTooltip = L.drawLocal.draw.handlers.polygon.tooltip;
  let pivot = L.drawLocal.draw.handlers.circle;
  if (context) {
    if (context.circle && context.rectangle) {
      context.circle._initialLabelText = getMessage('draw--drag-circle');
      context.circle._endLabelText = getMessage('draw--end-shape');
      context.rectangle._initialLabelText = getMessage('draw--drag-rectangle');
      context.rectangle._endLabelText = getMessage('draw--end-shape');
      polygonTooltip.start = getMessage('draw--start-shape');
      polygonTooltip.cont = getMessage('draw--continue-shape');
      polygonTooltip.end = getMessage('draw--close-shape');
      pivot.radius = getMessage('draw--circle-radius');
    }
  }
  return context;
};

export const _setContext = (map, context) => {
  map._DrawingContext = context;
};

export const _removeLastShapeDrawn = (map) => {
  const context = _getContext(map);
  if (context.lastShapeDrawnLayer) {
    context.lastShapeDrawnLayer.remove(map);
    context.lastShapeDrawnLayer = null;
  }
};

export const _addLastShapeDrawn = (map, shapeLayer) => {
  const context = _getContext(map);
  shapeLayer.addTo(map);
  context.lastShapeDrawnLayer = shapeLayer;
};

/*
 * Return GeoJson representation of a layer that contains one of the
 * supported types of shapes.
 */
export const _calcGeoJsonFromShapeLayer = (layer, shapeType) => {
  if (!layer) {
    return null;
  }
  if (shapeType === ShapeType.CIRCLE) {
    // calculate a polygon that approximates a circle.
    const CIRCLE_RESOLUTION = 28; // # of vertexes. Want smallest # that still looks smooth.
    const center = layer.getLatLng();
    const centerPoint = point([center.lng, center.lat]);
    return circle(centerPoint, layer.getRadius()/1000, CIRCLE_RESOLUTION, 'kilometers');
  } else {
    return layer.toGeoJSON();
  }
};

// When editing shapes, it generates very frequent events that could be CPU
// intensive for caller to process. This waits a period of time without any
// further edit events before notifying caller of new shape.

export const _handleEditEvent = ({map, shapeLayer, onDrawShapeComplete}) => {
  const context = _getContext(map);
  if (context.consolidateEditsTimer) {
    window.clearTimeout(context.consolidateEditsTimer);
  }
  context.consolidateEditsTimer = window.setTimeout(function() {
    const shapeGeoJson = _calcGeoJsonFromShapeLayer(shapeLayer, context.shapeType);
    onDrawShapeComplete(shapeGeoJson);
  }, timeouts.CONSOLIDATE_EDITS_WAIT_TIME);
};

export const _calcCircleToFitInBounds = (bounds, scaleRatio) => {
  const widthMeters = bounds.getNorthWest().distanceTo(bounds.getNorthEast());
  const heightMeters = bounds.getNorthWest().distanceTo(bounds.getSouthWest());

  const smallerBound = Math.min(widthMeters, heightMeters);
  const radius = smallerBound * scaleRatio;
  return L.circle(bounds.getCenter(), radius);
};

export const _calcRectangleToFitInBounds = (bounds, scaleRatio) => {
  // Anti-meridian wrapping for width is handled by getEast() returning a longitude greater than 180. So no special
  // case code needed.
  const widthDegrees = bounds.getEast() - bounds.getWest();
  const heightDegrees = bounds.getNorth() - bounds.getSouth();

  const center = bounds.getCenter();
  const xOffset = widthDegrees / 2 * scaleRatio;
  const yOffset = heightDegrees / 2 * scaleRatio;
  const rectBounds = [
    L.latLng(center.lat+yOffset, center.lng-xOffset),
    L.latLng(center.lat-yOffset, center.lng+xOffset)
  ];

  return L.rectangle(rectBounds);
};

// Add a shape to the map, update state to make it editable, and return geoJson representation of shape.
export const _addShapeAndEdit = (map, shapeLayer, shapeType) => {
  shapeLayer.addTo(map);
  _removeLastShapeDrawn(map);
  shapeLayer.editing.enable();
  _addLastShapeDrawn(map, shapeLayer);
  return _calcGeoJsonFromShapeLayer(shapeLayer, shapeType);
};

export const _addDefaultCircleAndEdit = (map, onDrawShapeComplete) => {
  const DEFAULT_CIRCLE_DROP_SCALE = 0.1;
  let shapeLayer = _calcCircleToFitInBounds(map.getBounds(),
    DEFAULT_CIRCLE_DROP_SCALE);
  let shapeGeoJson = _addShapeAndEdit(map, shapeLayer, _getContext(map).shapeType);
  if (shapeGeoJson && onDrawShapeComplete) {
    onDrawShapeComplete(shapeGeoJson);
  }
};

export const _addDefaultRectangleAndEdit = (map, onDrawShapeComplete) => {
  const DEFAULT_RECTANGLE_DROP_SCALE = 0.15;
  let shapeLayer = _calcRectangleToFitInBounds(map.getBounds(),
    DEFAULT_RECTANGLE_DROP_SCALE);
  let shapeGeoJson = _addShapeAndEdit(map, shapeLayer, _getContext(map).shapeType);
  if (shapeGeoJson && onDrawShapeComplete) {
    onDrawShapeComplete(shapeGeoJson);
  }
};

export const drawInit = ({map, onDrawShapeComplete}) => {
  let drawnItems = new L.FeatureGroup();
  map.addLayer(drawnItems);

  const drawOptions = map.drawControl.options;

  _setContext(map, {
    circle: new L.Draw.Circle(map, drawOptions.circle),
    rectangle:  new L.Draw.Rectangle(map, drawOptions.rectangle),
    polygon:    new L.Draw.Polygon(map, drawOptions.polygon),
    shapeType:  ShapeType.NONE,
    consolidateEditsTimer: null,
    lastShapeDrawnLayer: null
  });

  if (onDrawShapeComplete) {
    map.on('draw:created', function (e) {
      let shapeGeoJson = _addShapeAndEdit(map, e.layer, _getContext(map).shapeType);
      if (shapeGeoJson) {
        onDrawShapeComplete(shapeGeoJson);
      }
    });

    map.on('draw:editmove', function (e) {
      _handleEditEvent({map, shapeLayer:e.layer, onDrawShapeComplete});
    });

    map.on('draw:editresize', function (e) {
      _handleEditEvent({map, shapeLayer:e.layer, onDrawShapeComplete});
    });

    map.on('draw:editvertex', function (e) {
      _handleEditEvent({map, shapeLayer:e.poly, onDrawShapeComplete});
    });

    map.on('draw:drawstop', function () {
      let context = _getContext(map);
      if (!context.lastShapeDrawnLayer) {
        // Handling case where user has clicked map after starting a rectangle or circle but did not drag to indicate
        // an initial shape size. Drop a default shape that user may then edit.
        if (context.shapeType === ShapeType.SQUARE) {
          _addDefaultRectangleAndEdit(map, onDrawShapeComplete);
        } else if (context.shapeType === ShapeType.CIRCLE) {
          _addDefaultCircleAndEdit(map, onDrawShapeComplete);
        }
      }
    });
  }
};

export const drawDeinit = (map) => {
  map.off('draw:created');
  map.off('draw:editmove');
  map.off('draw:editresize');
  map.off('draw:editvertex');
  map.off('draw:drawstop');
  _setContext(map, null);
};

export const resetDrawing = (map) => {
  _removeLastShapeDrawn(map);
  const context = _getContext(map);
  context.shapeType = ShapeType.NONE;
  if (context.consolidateEditsTimer) {
    window.clearTimeout(context.consolidateEditsTimer);
    context.consolidateEditsTimer = null;
  }
  context.circle.disable();
  context.rectangle.disable();
  context.polygon.disable();
};

export const startCircle = (map) => {
  resetDrawing(map);
  const context = _getContext(map);
  context.circle.enable();
  context.shapeType = ShapeType.CIRCLE;
};

export const startRectangle = (map) => {
  resetDrawing(map);
  const context = _getContext(map);
  context.rectangle.enable();
  context.shapeType = ShapeType.SQUARE;
};

export const startPolygon = (map) => {
  resetDrawing(map);
  const context = _getContext(map);
  context.polygon.enable();
  context.shapeType = ShapeType.POLYGON;
};

export const exportedForUnitTesting = {
  _calcCircleToFitInBounds: _calcCircleToFitInBounds,
  _calcRectangleToFitInBounds: _calcRectangleToFitInBounds,
  _getContext: _getContext,
  _setContext: _setContext,
  _calcGeoJsonFromShapeLayer: _calcGeoJsonFromShapeLayer
};


export const setCircleRadiusUnit = (map, unitsOfMeasure) => {
  // By default, the circle radius unit is feet (imperial)
  // if the user has as unit of measure SI or Canada
  // we change the unit to metric
  if (unitsOfMeasure !== UNITS_OF_MEASURE.imperial) {
    map.drawControl.options.circle = {
      metric: true,
      feet: false,
      nautic: false
    };
  }
};
